import { useEffect, useMemo } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import { arrayOf, bool, string, shape, func } from 'prop-types';
import { WARNING } from '#action/types';

/**
 * Redirects users without projects to profile page.
 * Dispatches warning message on redirect.
 */

const RedirectUserWithoutProjects = props => {
  const { unprotectedRoutes, isRouteMatches } = props;
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const {
    directProjects: projects,
    requestStatus,
    id: accountId
  } = useSelector(s => s.account, shallowEqual);

  const userHasAnyProjects = !!projects?.length;
  const isProjectsLoading = ['idle', 'loading'].includes(requestStatus);

  const redirectConditions = useMemo(
    () => [
      (!isProjectsLoading && !userHasAnyProjects),
      unprotectedRoutes?.every(ur => !isRouteMatches?.(ur)),
      location.pathname !== `/profile/${accountId}`
    ],
    [
      userHasAnyProjects,
      isProjectsLoading,
      unprotectedRoutes,
      location,
      accountId,
      isRouteMatches
    ]
  );

  useEffect(() => {
    if (redirectConditions.every(Boolean)) {
      dispatch({
        type: WARNING,
        payload: {
          message: 'Seems like you are not assigned to any projects, we safely redirect you to the profile page'
        }
      });
    }
  }, [dispatch, redirectConditions]);

  if (redirectConditions.every(Boolean)) {
    setTimeout(() => {
      history.replace(`/profile/${accountId}`);
    }, 0);
    return null;
  }

  return props.children;
};

RedirectUserWithoutProjects.propTypes = {
  unprotectedRoutes: arrayOf(shape({
    path: string.isRequired,
    isExact: bool
  })).isRequired,
  isRouteMatches: func.isRequired
};

export default RedirectUserWithoutProjects;
